import clsx from 'clsx';
import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Draggable.module.scss';
import { ensurePortalRoot } from './draggable-helpers.ts';
import { useDraggableEvents } from './useDraggableEvents.ts';
import {
  DraggableOffsets,
  DraggablePlacement,
  DraggablePosition,
  Horizontal,
  useDraggablePosition,
  Vertical,
  VIEWPORT_GAP,
} from './useDraggablePositions.ts';

export type DraggableProps = PropsWithChildren<{
  placement?: DraggablePlacement;
  offset?: DraggableOffsets;

  // You can drag elements wrapped in 'Draggable' only when you hold down the header.
  header?: ReactNode;

  headerClassName?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  hidden?: boolean;
}>;

export const Draggable = (props: DraggableProps) => {
  const {
    children,
    placement = { horizontal: Horizontal.Left, vertical: Vertical.Bottom },
    offset,
    header,
    headerClassName,
    className,
    contentClassName,
    disabled = false,
    hidden = false,
  } = props;

  const draggableDivRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState<DraggablePosition | null>(null);

  // Initializing portal root element
  useEffect(() => {
    setPortalRoot(ensurePortalRoot());
  }, []);

  useDraggablePosition({
    placement,
    offset,
    portalRoot,
    draggableDivRef,
    setPosition,
    hidden,
  });

  const {
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  } = useDraggableEvents({
    disabled,
    draggableDivRef,
    setPosition,
  });

  if (!portalRoot) {
    return null;
  }

  const style: CSSProperties = {
    '--viewport-gap': `${VIEWPORT_GAP}px`,
    ...(position && {
      left: `${position.left}px`,
      top: `${position.top}px`,
    }),
  } as CSSProperties;

  return createPortal(
    <div
      ref={draggableDivRef}
      className={clsx(
        styles.draggableContainer,
        hidden && styles.hidden,
        className,
      )}
      style={style}
    >
      {header && (
        <div
          ref={headerRef}
          className={clsx(
            styles.header,
            headerClassName,
            isDragging && styles.dragging,
            disabled && styles.draggingDisabled,
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          {header}
        </div>
      )}
      <div className={clsx(styles.content, contentClassName)}>{children}</div>
    </div>,
    portalRoot,
  );
};
